import type {
    AssessmentResponse,
    ChecklistItem,
    ItemScore,
    SurveyAnswer,
} from '~/api/types'
import {
    checklistCriteriaSections,
    type ChecklistCriteriaSection,
} from '~/lib/checklist-criteria'

// 채점 근거 설명 헬퍼는 레이어 중립 lib에 두고, 이 배럴에서 재노출한다.
export {
    passLikelihoodBasis,
    SCORING_CRITERIA_NOTE,
} from '~/lib/scoring-basis'

export function formatChecklistItemLabel(item: ChecklistItem): string {
    return `${item.itemNumber ? `${item.itemNumber}. ` : ''}${item.title}`
}

export function formatJumpTargetMessage(
    assessment: AssessmentResponse,
    item: ChecklistItem
): string {
    const categoryPath = [
        assessment.industry,
        item.majorCategory,
        item.middleCategory,
    ]
        .filter(Boolean)
        .join(' - ')
    const itemLabel = formatChecklistItemLabel(item)

    return categoryPath
        ? `[${categoryPath}] ${itemLabel} 문항으로 이동합니다.`
        : `${itemLabel} 문항으로 이동합니다.`
}

export function itemCategoryPath(item: ChecklistItem): string {
    return [item.part, item.majorCategory, item.middleCategory]
        .filter(Boolean)
        .join(' · ')
}

export function displayAnswerValue(answer?: SurveyAnswer): string {
    if (!answer) return '미응답'
    if (answer.unknown) return '모름/해당없음'
    const value = answer.value?.trim()
    if (!value) return '미응답'
    if (value.toUpperCase() === 'TRUE') return '예'
    if (value.toUpperCase() === 'FALSE') return '아니오'
    return value
}

export function resultMethodLabel(method: ItemScore['method']): string {
    return method === 'deterministic' ? '규칙 채점' : 'AI 채점'
}

export type ChecklistDetailSection = ChecklistCriteriaSection

/** 인증 결과 상세의 평가 기준 섹션 — 단일 출처(`checklistCriteriaSections`)에 위임한다. */
export function splitChecklistDetail(
    item: ChecklistItem
): ChecklistDetailSection[] {
    return checklistCriteriaSections(item)
}

export function answerRows(item: ChecklistItem, answers: SurveyAnswer[]) {
    const byField = new Map(answers.map((answer) => [answer.fieldId, answer]))
    if (item.fields.length === 0) {
        return [
            {
                key: `item:${item.id}`,
                label: '항목 답변',
                question: item.title,
                value: displayAnswerValue(byField.get(`item:${item.id}`)),
            },
        ]
    }
    return item.fields.map((field) => ({
        key: field.fieldId,
        label: field.nameKo,
        question: field.question,
        value: displayAnswerValue(byField.get(field.fieldId)),
    }))
}

export function scoreTotal(scores: ItemScore[]) {
    return scores.reduce(
        (acc, score) => ({
            score: acc.score + (score.score ?? 0),
            maxScore: acc.maxScore + score.maxScore,
        }),
        { score: 0, maxScore: 0 }
    )
}

export function verdictCount(
    scores: ItemScore[],
    verdict: ItemScore['verdict']
) {
    return scores.filter((score) => score.verdict === verdict).length
}

export function scoreRatio(score: ItemScore): number {
    if (score.maxScore <= 0) return 0
    return (score.score ?? 0) / score.maxScore
}

export function priorityWeight(score: ItemScore): number {
    if (score.verdict === '부적합') return 0
    if (score.verdict === '미흡') return 1
    return 2
}

export function priorityScores(scores: ItemScore[]): ItemScore[] {
    return [...scores].sort((a, b) => {
        const verdictDiff = priorityWeight(a) - priorityWeight(b)
        if (verdictDiff !== 0) return verdictDiff
        return scoreRatio(a) - scoreRatio(b)
    })
}

export function scoreToneClass(total: number, max: number): string {
    if (max <= 0) return 'text-foreground'
    const ratio = total / max
    if (ratio >= 0.8) return 'text-success'
    if (ratio >= 0.5) return 'text-warning'
    return 'text-destructive'
}

export function compactAnswerText(
    item: ChecklistItem | undefined,
    answers: SurveyAnswer[]
): string {
    if (!item) return '체크리스트 항목 정보를 불러오지 못했습니다.'
    const rows = answerRows(item, answers)
        .filter((row) => row.value !== '미응답')
        .slice(0, 3)
    if (rows.length === 0) return '아직 이 항목에 연결된 답변이 없습니다.'
    return rows.map((row) => `${row.label}: ${row.value}`).join('\n')
}
